/**
 * 管理特性开关状态
 * 文档 https://doc.weixin.qq.com/doc/w3_Ae4ADwbBAJsr8vXr2OCS16Iek4nES?scode=AJEAIQdfAAo0jXZbnlAe4ADwbBAJs
 */

import { createNanoEvents } from 'nanoevents';
import ConfigSDK from '@tencent/config-sdk';
import { getCookie } from 'sutils';
import { SWITCH_STATE, SWITCH_ENV_ID, SWITCH_KEY } from './contant';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';
import { shouldDisableNetwork } from '@src/slide/infrastructure/module/dependent/socket';
import appConfigInstance from '@src/slide/infrastructure/module/dependent/config/tob/app-config';

const MAX_INIT_RETRY_TIMES = 1;
const DEFAULT_RETRY_PERIOD = 1000;

enum SwitchEvent {
    READY = 'ready'
}

interface ConfigMapItem {
    value: boolean;
}

export default class SwitchSystemManager {
    // 开关的缓存，只需要初始化拉一次配置的开关项存在这里
    public static configMap = new Map<SWITCH_KEY, ConfigMapItem>();
    private static configSDK: ConfigSDK;
    private static envName = getCookie('env_name');
    private static isReady = false;
    private static events = createNanoEvents();
    private static initRetryTimes = 0;

    /**
     * 初始化特性开关 sdk
     */
    public static async initConfigSDK(): Promise<void> {
        this.configSDK = new ConfigSDK({
            systemType: 'config',
            appID: 'b498425e61',
            appToken: 'eaf75276-fda5-42b1-9caf-e2504bf711e8',
            // 该平台的迷惑配置项，想拉测试环境的配置则test字段必须传false，已反馈，后续可能优化
            test: false,
            guid: global.basicClientVars?.userInfo?.tinyId,
            env: this.envName ? SWITCH_ENV_ID.DEFAULT : '',
        });
        // localStorage 初始化 map
        const switchValues = userLocalStorage.getItem(LocalStorageKeys.SHIPLY_SWITCH_VALUE);
        if (switchValues) {
            Object.entries(switchValues).forEach(([key, value]) => {
                this.configMap.set(key as SWITCH_KEY, { value });
            });
        }
        // 私有化、webOffice关闭此开关
        if (appConfigInstance.getConfig('custom.configSDKPullDisabled' as any) ?? false) return;
        this.getValuesFromBackEndByKeys(Object.values(SWITCH_KEY));
    }

    /**
     * 获取开关值
     * @param key 开关的名称，需确保在 shiply 中已配置
     * @param cb 开关初始化完成后的回调函数，若要保证在拉取完开关状态后才去取值，需要在cb函数里取，否则可能取的时候尚未拉取到开关值而返回之前的缓存值
     * @returns {boolean} 开关的状态，若尚未拉取到开关状态，返回旧的缓存值。
     * 在首次使用中，由于没有 localStorage 缓存，会返回 false。
     * 在开关变更后，旧的缓存值会和拉取的值不同，若要获取到新的开关状态，可以在回调函数 cb 里取开关值。
     */
    public static getKeyValueFromMap(key: SWITCH_KEY, cb?: (value: boolean) => void): boolean {
        const configMapItem = this.configMap.get(key);
        // 没有缓存过这个key，重新拉取
        if (!configMapItem) {
            cb && this.onReady(key, cb);
            return false;
        }
        // 之前拉取过，直接使用缓存值
        const { value } = configMapItem;
        cb && this.onReady(key, cb);
        return value;
    }

    // 批量拉取开关
    private static async getValuesFromBackEndByKeys(keys: SWITCH_KEY[]) {
        try {
            if (shouldDisableNetwork()) {
                return;
            }
            const { configs } = await this.configSDK.pull({ keys }); // 可以配置 pull 的 guid 参数来执行灰度开关
            // 重试逻辑
            if (!configs) {
                if (this.initRetryTimes < MAX_INIT_RETRY_TIMES) {
                    this.initRetryTimes += 1;
                    setTimeout(() => {
                        this.getValuesFromBackEndByKeys(keys);
                    }, DEFAULT_RETRY_PERIOD);
                }
                return;
            }
            // 解析拿到的开关配置
            keys.forEach((key) => {
                const result = this.isSwitchStateOn(configs, key);
                this.configMap.set(key, { value: result });
                this.setLocalStorage(key, result);
            });
            this.isReady = true;
            this.events.emit(SwitchEvent.READY);
        } catch (err) {
            // 重试逻辑
            if (this.initRetryTimes < MAX_INIT_RETRY_TIMES) {
                this.initRetryTimes += 1;
                setTimeout(() => {
                    this.getValuesFromBackEndByKeys(keys);
                }, DEFAULT_RETRY_PERIOD);
            }
            window.log.error(err);
        }
    }

    // 将开关值解析为 true or false
    private static isSwitchStateOn(configs, key: SWITCH_KEY) {
        if (!Array.isArray(configs)) {
            return false;
        }
        try {
            // eslint-disable-next-line no-restricted-syntax
            for (const config of configs) {
                if (config.key === key) {
                    const value = JSON.parse(config.value);
                    return value.switch_state === SWITCH_STATE.ON;
                }
            }
        } catch (err) {
            window.log.error(err);
        }
        return false;
    }

    // 保存开关值到 localStorage
    private static setLocalStorage(key: SWITCH_KEY, value: boolean) {
        const switchValue = userLocalStorage.getItem(LocalStorageKeys.SHIPLY_SWITCH_VALUE);
        if (switchValue) {
            switchValue[key] = value;
            userLocalStorage.setItem(LocalStorageKeys.SHIPLY_SWITCH_VALUE, switchValue);
        } else {
            const newValue = { [key]: value };
            userLocalStorage.setItem(LocalStorageKeys.SHIPLY_SWITCH_VALUE, newValue);
        }
    }

    // 注册开关拉取后执行的函数
    private static onReady(key: SWITCH_KEY, cb: (value: boolean) => void) {
        if (this.isReady) {
            const configMapItem = this.configMap.get(key) ?? { value: false };
            cb(configMapItem.value);
            return;
        }
        this.events.on(SwitchEvent.READY, () => {
            const configMapItem = this.configMap.get(key) ?? { value: false };
            cb(configMapItem.value);
        });
    }
}
